import type { ReactNode } from 'react';
import { formatAddress, mapsUrl, type Property } from '@fsm/shared';
import { Avatar, Card, cx } from './ui';
import { Icon, type IconName } from './icons';
import { PhoneLine } from './CallActions';

/**
 * Who this record is for.
 *
 * The same panel opens the job, the estimate, the invoice and the customer:
 * a name, how to reach them, and where the work is. It was written out four
 * times, four slightly different ways — which is how one of them ended up
 * without a way to text and another without the gate code.
 *
 * Each row is a label with its value beneath and a small glyph at the left,
 * rather than a definition list: a phone number wants to be the biggest thing
 * on its line, and "Phone" only needs to be legible.
 */
export function ContactCard({
  title = 'Customer',
  name,
  role,
  phone,
  email,
  property,
  customerId,
  jobId,
  addressLabel = 'Service address',
  footLink,
  children,
}: {
  title?: string;
  name: string;
  /** "Residential customer" — what kind of account this is. */
  role?: string;
  phone?: string | null;
  email?: string | null;
  property?: Property | null;
  customerId?: string;
  jobId?: string;
  addressLabel?: string;
  footLink?: { to: string; label: string };
  children?: ReactNode;
}) {
  return (
    <Card
      title={title}
      action={<Icon name="users" size={17} className="text-ink-4" />}
      footLink={footLink}
      bodyClassName="px-[21px] pt-1 pb-[21px]"
    >
      <div className="flex items-center gap-3 pb-5">
        <Avatar name={name} color="#eae7de" textColor="#8a7958" size={41} />
        <div className="min-w-0">
          <div className="truncate text-[16px] font-semibold text-ink">{name}</div>
          {role && <div className="mt-0.5 text-[12px] text-ink-4">{role}</div>}
        </div>
      </div>

      {phone && (
        <ContactRow icon="phone" label="Phone">
          <PhoneLine phone={phone} customerId={customerId} jobId={jobId} customerName={name} />
        </ContactRow>
      )}

      {email && (
        <ContactRow icon="mail" label="Email">
          <a
            href={`mailto:${email}`}
            className="block text-[14px] break-words text-ink-2 hover:text-accent hover:underline"
          >
            {email}
          </a>
        </ContactRow>
      )}

      {property && (
        <ContactRow icon="pin" label={addressLabel} divided>
          <a
            href={mapsUrl(property, 'other')}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-start gap-1.5 text-[14px] leading-relaxed text-ink-2 hover:text-accent"
          >
            <span>{formatAddress(property)}</span>
            <Icon name="arrow" size={12} className="mt-1.5 opacity-60" />
          </a>
          {property.gateCode && (
            <div className="mt-1 text-[12px] text-ink-4">Gate code {property.gateCode}</div>
          )}
        </ContactRow>
      )}

      {children}
    </Card>
  );
}

function ContactRow({
  icon,
  label,
  divided,
  children,
}: {
  icon: IconName;
  label: string;
  /** A hairline above — used to set the address apart from the contact details. */
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cx(
        'flex gap-2.5 pb-[18px] last:pb-0',
        divided && 'border-t border-line pt-[17px]',
      )}
    >
      <Icon name={icon} size={15} className="mt-1 text-ink-4" />
      <div className="min-w-0 flex-1">
        <div className="mb-1 text-[14px] text-ink-3">{label}</div>
        {children}
      </div>
    </div>
  );
}
